import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SortIcon from "@mui/icons-material/Sort";
import {
  staffList,
  deleteStaff,
  updateStaff,
} from "../../../Services/Services";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import AddNewUser from "./AddNewUser";
import Popover from "@mui/material/Popover";

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [editUserId, setEditUserId] = useState(null); // Use userId for editing
  const [editUserData, setEditUserData] = useState({});
  const [openEditModal, setOpenEditModal] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [mobileError, setMobileError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await staffList(); // don't pass any argument if not needed
        setUsers(response);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();
  }, []);

  const getFilteredSortedUsers = () => {
    let filtered = users.filter(
      (user) =>
        user.name?.toLowerCase().includes(filter.toLowerCase()) ||
        user.email?.toLowerCase().includes(filter.toLowerCase())
    );

    filtered.sort((a, b) => {
      const nameA = a.name?.toLowerCase() || "";
      const nameB = b.name?.toLowerCase() || "";
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return filtered;
  };

  const handleDelete = async (userId) => {
    try {
      const response = await deleteStaff(userId); // don't pass any argument if not needed
      setUsers(response);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleEdit = (userId) => {
    const userToEdit = users.find((user) => user.user_id === userId);

    setEditUserData({ ...userToEdit });
    setEditUserId(userId);
    setOpenEditModal(true);
  };

  function formatOnlyDate(dateTimeString, format = "dd-mm-yyyy") {
    const date = new Date(dateTimeString);

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    if (format === "dd-mm-yyyy") {
      return `${day}-${month}-${year}`;
    } else if (format === "yyyy-mm-dd") {
      return `${year}-${month}-${day}`;
    } else {
      throw new Error("Unsupported format. Use 'dd-mm-yyyy' or 'yyyy-mm-dd'.");
    }
  }

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "mo_no") {
      const onlyDigits = value.replace(/\D/g, "");
      if (onlyDigits.length <= 10) {
        setEditUserData((prev) => ({ ...prev, [name]: onlyDigits }));
      }
    } else {
      setEditUserData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validateMobile = (mobile) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(mobile);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSaveEdit = async () => {
    if (!validateMobile(editUserData.mo_no)) {
      setMobileError("Please enter a valid 10-digit mobile number");
      return;
    } else {
      setMobileError("");
    }

    if (!validateEmail(editUserData.email)) {
      setEmailError("Please enter a valid email address");
      return;
    } else {
      setEmailError("");
    }

    try {
      const response = await updateStaff(editUserId, editUserData); // don't pass any argument if not needed
      setUsers(response);
      setOpenEditModal(false);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCancelEdit = () => {
    setOpenEditModal(false);
  };

  const handleTogglePassword = (userId) => {
    setShowPassword((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printable-table");
    const WinPrint = window.open("", "", "width=900,height=650");
    WinPrint.document.write(`
      <html>
      <head>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 0px 20px 0px 20px;
            position: relative;
          }
          header {
            text-align: center;
            font-size: 20px;
            font-weight: bold;
            margin-bottom: 20px;
            color: #333;
          }
          footer {
            position: fixed;
            bottom: 10px;
            width: 100%;
            text-align: center;
            font-size: 12px;
            color: #333;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid black;
            padding: 8px;
            text-align: center;
            font-size: 12px;
          }
          th {
              background-color:rgba(211, 211, 211, 0.42); /* light grey */
              color: #000;
              -webkit-print-color-adjust: exact; /* For Chrome */
              print-color-adjust: exact;         /* For Firefox */
            }
          .no-print {
            display: none;
          }
        </style>
      </head>
      <body>
        <header>SR ENTERPRICES</header>
        <h3 style="text-align: center;">Staff List</h3>
          ${printContent.innerHTML}
        <footer>Powered by : YASHVITECH IT SOLUTION PVT LTD , 8223079476 </footer>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() {
              window.close();
            };
          };
        </script>
      </body>
    </html>
    `);
    WinPrint.document.close();
    WinPrint.focus();
    WinPrint.print();
    WinPrint.close();
  };

  const handleDownloadExcel = () => {
    const dataToExport = getFilteredSortedUsers().map(
      ({ user_id, name, mo_no, email, address }, i) => ({
        "S. No.": i + 1,
        "User ID": user_id,
        Name: name,
        "Mobile No": mo_no,
        Email: email,
        Address: address,
      })
    );

    const headers = Object.keys(dataToExport[0]);
    const worksheetData = [
      headers,
      ...dataToExport.map((obj) => Object.values(obj)),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-fit column widths
    const columnWidths = headers.map((_, colIndex) => {
      const maxLength = worksheetData.reduce((max, row) => {
        const cell = row[colIndex];
        const len = cell ? cell.toString().length : 10;
        return Math.max(max, len);
      }, headers[colIndex].length);
      return { wch: maxLength + 2 }; // add padding
    });
    worksheet["!cols"] = columnWidths;

    // Freeze the header row
    worksheet["!freeze"] = { xSplit: 0, ySplit: 1 };

    const blackBorder = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    // Style header row
    headers.forEach((_, colIdx) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIdx })];
      if (cell) {
        cell.s = {
          fill: { fgColor: { rgb: "FFFF99" } },
          font: { bold: true, color: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: blackBorder,
        };
      }
    });

    // Style data cells
    for (let r = 1; r < worksheetData.length; r++) {
      for (let c = 0; c < headers.length; c++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (cell) {
          cell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: blackBorder,
          };
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "UserList.xlsx");
  };

  return (
    <div style={{ width: "100%", maxHeight: "calc(100vh - 70px)" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          py: 1,
          mb: 2,
          position: "sticky",
          top: 10,
          zIndex: 1000,
        }}
      >
        <TextField
          label="Search by name or email"
          variant="outlined"
          size="small"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ width: "250px" ,marginTop: -4}}
        />

        <Box
          sx={{
            border: "1px solid #ccc",
            padding: "10px 12px",
            borderRadius: "8px",
            whiteSpace: "nowrap",
            width: "300px",
            height: "40px",
            marginTop: -4
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, }}>
            Total Users: {getFilteredSortedUsers().length}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          sx={{ width: "220px",marginTop: -4,maxHeight:"38px" }}
          startIcon={<SortIcon />}
          onClick={() => setSortAsc(!sortAsc)}
        >
          Sort by Name ({sortAsc ? "A-Z" : "Z-A"})
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAddModal(true)}
          sx={{ marginTop: -4 ,maxHeight:"38px"}}
        >
          Add New Staff
        </Button>
        <Button
          variant="outlined"
          sx={{ minWidth: "40px", padding: 0.8,marginTop: -4 ,maxHeight:"38px"}}
          onClick={handlePrint}
        >
          <PrintIcon fontSize="small" />
        </Button>
        <Button
          variant="outlined"
          sx={{ minWidth: "40px", padding: 0.8 ,marginTop: -4 ,maxHeight:"38px"}}
          onClick={handleDownloadExcel}
        >
          <FileDownloadIcon fontSize="small" />
        </Button>
      </Box>

      {/* Display Users in Table */}
      {users.length === 0 ? (
        <Typography>No users found.</Typography>
      ) : (
        <div id="printable-table">
          <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
            <div style={{ maxHeight: "75vh", overflowX: "auto" }}>
              <Table
                stickyHeader
                size="small"
                sx={{ "& td, & th": { borderBottom: "1px solid #ccc" } }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell align="center">S. No.</TableCell>
                    {/* <TableCell align="center">User ID</TableCell> */}
                    <TableCell align="center">Name</TableCell>
                    <TableCell align="center">Mobile No</TableCell>
                    <TableCell align="center">Email</TableCell>
                    {/* <TableCell align="center">Password</TableCell> */}
                    <TableCell align="center">Address</TableCell>
                    <TableCell align="center">Created</TableCell>
                    {/* <TableCell align="center">Updated</TableCell> */}
                    <TableCell align="center" className="no-print">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredSortedUsers().map((user, index) => (
                    <TableRow
                      key={user.userId}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff", // alternate row color
                        "& td": {
                          paddingTop: "0px",
                          paddingBottom: "0px",
                          fontSize: "0.8125rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: 120,
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      <TableCell align="center">{index + 1}</TableCell>
                      {/* <TableCell align="center">{user.user_id || 'N/A'}</TableCell> */}
                      <TableCell align="center">{user.name}</TableCell>
                      <TableCell align="center">{user.mo_no}</TableCell>
                      <TableCell align="center">{user.email}</TableCell>

                      <TableCell align="center">{user.address}</TableCell>
                      <TableCell align="center">
                        {formatOnlyDate(user.created_at)}
                      </TableCell>
                      {/* <TableCell align="center">{formatOnlyDate(user.updated_at)}</TableCell> */}
                      <TableCell align="center" className="no-print">
                        <IconButton
                          onClick={() => handleEdit(user.user_id)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setUserToDelete(user.user_id);
                          }}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>

                        {/* <IconButton onClick={() => handleDelete(user.user_id)} color="error" size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton> */}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TableContainer>
        </div>
      )}

      <Dialog open={openEditModal} onClose={handleCancelEdit}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            name="name"
            label="Name"
            fullWidth
            value={editUserData.name || ""}
            onChange={handleEditChange}
          />
          <TextField
            margin="dense"
            name="mo_no"
            label="Mobile No"
            fullWidth
            value={editUserData.mo_no || ""}
            onChange={handleEditChange}
            error={!!mobileError}
            helperText={mobileError}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            fullWidth
            value={editUserData.email || ""}
            onChange={handleEditChange}
            error={!!emailError}
            helperText={emailError}
          />
          <TextField
            margin="dense"
            name="address"
            label="Address"
            fullWidth
            value={editUserData.address || ""}
            onChange={handleEditChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Staff</DialogTitle>
        <DialogContent dividers>
          <AddNewUser setUsers={setUsers} setOpenAddModal={setOpenAddModal} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setUserToDelete(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(userToDelete);
                } finally {
                  setAnchorEl(null);
                  setUserToDelete(null);
                }
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setUserToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default UserList;
